import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';

interface GoogleImportProps {
    onContactsUpdate?: () => void;
}

export default function GoogleImport({ onContactsUpdate }: GoogleImportProps) {
    const [googleSyncing, setGoogleSyncing] = useState(false);

    const handleGoogleAuth = async () => {
        try {
            console.log('🔐 Starting Google OAuth flow...');

            const response = await fetch('https://hnxenvvnlekhknvjuagb.supabase.co/functions/v1/google-contacts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'getAuthUrl'
                })
            });

            const data = await response.json();

            if (!data.success || !data.authUrl) {
                throw new Error('Failed to get Google authorization URL');
            }

            // Open Google OAuth in the same window (easier for desktop)
            window.location.href = data.authUrl;

        } catch (error) {
            console.error('Google OAuth error:', error);
            alert('Failed to start Google authorization. Please make sure the Google Cloud Console is properly configured.');
        }
    };

    const handleGoogleSync = async () => {
        if (googleSyncing) return;

        try {
            setGoogleSyncing(true);

            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                alert('Please log in to sync with Google Contacts');
                return;
            }

            const response = await fetch('https://hnxenvvnlekhknvjuagb.supabase.co/functions/v1/google-contacts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'sync',
                    userId: user.id
                })
            });

            const data = await response.json();

            if (data.success) {
                alert(`Successfully synced ${data.syncedCount} contacts from Google!`);
                // Refresh contacts list
                if (onContactsUpdate) {
                    onContactsUpdate();
                }
            } else {
                throw new Error(data.error || 'Failed to sync contacts');
            }

        } catch (error) {
            console.error('Google sync error:', error);
            alert('Failed to sync with Google Contacts. Please make sure you have authorized the application.');
        } finally {
            setGoogleSyncing(false);
        }
    };

    return (
        <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">Google Contacts Import</h3>

            <div className="space-y-4">
                <p className="text-sm text-gray-300">
                    Import contacts directly from your Google Contacts account. You'll need to authorize access first.
                </p>

                <div className="flex gap-3">
                    <button
                        onClick={handleGoogleAuth}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Authorize Google Account
                    </button>

                    <button
                        onClick={handleGoogleSync}
                        disabled={googleSyncing}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {googleSyncing ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Syncing...
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Sync Google Contacts
                            </>
                        )}
                    </button>
                </div>

                <div className="bg-gray-800 p-4 rounded-lg">
                    <h4 className="text-md font-medium text-white mb-2">How it works:</h4>
                    <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside">
                        <li>Click "Authorize Google Account" to grant permission</li>
                        <li>You'll be redirected to Google to sign in and approve access</li>
                        <li>Return here and click "Sync Google Contacts" to import</li>
                        <li>Your contacts will be automatically imported and deduplicated</li>
                    </ol>
                </div>

                <div className="bg-blue-900 p-4 rounded-lg">
                    <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <h5 className="text-sm font-medium text-blue-300">Privacy & Security</h5>
                            <p className="text-sm text-blue-200 mt-1">
                                We only access your contact information to import it into your private vault.
                                No data is shared with third parties, and you can revoke access at any time.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
